import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/auth';
import { auth } from 'firebase/app';
import { take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { PhoneNumber } from '../models/auth/phone-number';
import { AppError, AppErrorType } from '../pages/shared/errors/errors';

@Injectable({ providedIn: 'root' })
export class AuthService {
  constructor(private afAuth: AngularFireAuth) {
    if (environment.stage === 'development') {
      this.afAuth.auth.settings.appVerificationDisabledForTesting = true;
    }
  }

  verify(): Promise<firebase.User | null> {
    return this.afAuth.user.pipe(take(1)).toPromise();
  }

  isSignedIn(): boolean {
    return this.afAuth.auth.currentUser !== null;
  }

  getCurrentUser(): firebase.User | null {
    return this.afAuth.auth.currentUser;
  }

  reloadCurrentUser(): Promise<void> {
    const currentUser = this.getCurrentUser();
    if (currentUser === null) {
      throw new AppError(AppErrorType.CurrentUserNotFound);
    }

    return currentUser.reload();
  }

  getToken(): Promise<string | undefined> {
    const currentUser = this.getCurrentUser();
    if (currentUser === null) {
      throw new AppError(AppErrorType.CurrentUserNotFound);
    }

    return currentUser.getIdToken();
  }

  /**
   * Firebase Authentication への Sign In を実行する。
   * https://firebase.google.com/docs/auth/?hl=ja
   *
   * @param recaptureContainer reCAPTCHA を格納する DOM の id 属性。
   *   https://firebase.google.com/docs/reference/js/firebase.auth.RecaptchaVerifier.html
   * @param phoneNumber 認証を行う電話番号。
   */
  signIn(
    recaptureContainer: string,
    phoneNumber: PhoneNumber,
  ): Promise<auth.ConfirmationResult> {
    const recapture = new auth.RecaptchaVerifier(recaptureContainer, {
      size: 'invisible',
    });

    return this.afAuth.auth.signInWithPhoneNumber(
      phoneNumber.toE164(),
      recapture,
    );
  }

  signInWithEmailAndPassword(
    email: string,
    password: string,
  ): Promise<auth.UserCredential> {
    return this.afAuth.auth.signInWithEmailAndPassword(email, password);
  }

  // 電話番号変更のための verificationId を発行する.
  verifyPhoneNumber(
    recaptureContainer: string,
    phoneNumber: PhoneNumber,
  ): Promise<string> {
    const recapture = this.createRecapture(recaptureContainer);
    const provider = new auth.PhoneAuthProvider();
    return provider.verifyPhoneNumber(phoneNumber.toE164(), recapture);
  }

  // 電話番号を変更する.
  updatePhoneNumber(
    verificationId: string,
    verificationCode: string,
  ): Promise<void> {
    const currentUser = this.getCurrentUser();

    if (currentUser === null) {
      throw new AppError(AppErrorType.CurrentUserNotFound);
    }

    const credential = auth.PhoneAuthProvider.credential(
      verificationId,
      verificationCode,
    );

    return currentUser.updatePhoneNumber(credential);
  }

  createRecapture(recaptureContainer: string): auth.RecaptchaVerifier {
    return new auth.RecaptchaVerifier(recaptureContainer, {
      size: 'invisible',
    });
  }

  // 再認証
  reauthenticateWithPhoneNumber(
    recapture: auth.RecaptchaVerifier,
    phoneNumber: PhoneNumber,
  ): Promise<firebase.auth.ConfirmationResult> {
    const currentUser = this.getCurrentUser();
    if (currentUser === null) {
      throw new AppError(AppErrorType.CurrentUserNotFound);
    }

    return currentUser.reauthenticateWithPhoneNumber(
      phoneNumber.toE164(),
      recapture,
    );
  }

  sendPasswordResetMail(email: string): Promise<void> {
    return this.afAuth.auth.sendPasswordResetEmail(email);
  }

  /**
   * 退会処理を実行する.
   */
  withdraw(): Promise<void> {
    const currentUser = this.getCurrentUser();
    if (currentUser === null) {
      throw new AppError(AppErrorType.CurrentUserNotFound);
    }

    return currentUser.delete();
  }

  confirm(
    confirmationResult: auth.ConfirmationResult,
    verificationCode: string,
  ): Promise<auth.UserCredential> {
    return confirmationResult.confirm(verificationCode);
  }

  createEmailAuthCredential(
    email: string,
    password: string,
  ): auth.AuthCredential {
    return auth.EmailAuthProvider.credential(email, password);
  }

  signOut(): Promise<void> {
    return this.afAuth.auth.signOut();
  }
}
